// In-memory mock inbox mirroring the notifications API contract. Pure and
// framework-free so it can back api.ts in dev and be unit-tested directly.
// State is module-level and mutable within a session (mark-read persists).

import type {
  NotificationItem,
  NotificationPage,
  NotificationPreferences,
  NotificationPreferencesPatch,
  NotificationType,
} from "./model.js";
import { NOTIFICATION_TYPES } from "./model.js";
import { buildMockNotifications } from "./mocks.js";

let store: NotificationItem[] = buildMockNotifications();

function allTypesEnabled(): Record<NotificationType, boolean> {
  const types = {} as Record<NotificationType, boolean>;
  for (const t of NOTIFICATION_TYPES) types[t] = true;
  return types;
}

let mockPreferences: NotificationPreferences = {
  notificationPush: true,
  types: allTypesEnabled(),
};

/** Reset the store to a fresh seed (used by tests and dev tooling). */
export function resetMockStore(now: Date = new Date()): void {
  store = buildMockNotifications(now);
  mockPreferences = { notificationPush: true, types: allTypesEnabled() };
}

export interface ListMockParams {
  before?: string | null;
  type?: NotificationType | null;
  limit?: number;
}

/** Keyset-paginated list, newest first, matching listInbox on the backend. */
export function listMockNotifications({
  before,
  type,
  limit = 20,
}: ListMockParams = {}): NotificationPage {
  let rows = [...store].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  if (type) rows = rows.filter((n) => n.type === type);
  if (before) {
    const cursor = new Date(before).getTime();
    rows = rows.filter((n) => new Date(n.createdAt).getTime() < cursor);
  }
  const capped = Math.max(1, Math.min(limit, 100));
  const items = rows.slice(0, capped);
  const hasMore = rows.length > capped;
  const nextBefore = hasMore ? items[items.length - 1].createdAt : null;
  return { items, nextBefore };
}

export function getMockUnreadCount(): number {
  return store.filter((n) => n.readAt === null).length;
}

export function markMockRead(id: string): void {
  store = store.map((n) =>
    n.id === id && n.readAt === null
      ? { ...n, readAt: new Date().toISOString() }
      : n,
  );
}

export function markAllMockRead(): void {
  const readAt = new Date().toISOString();
  store = store.map((n) => (n.readAt ? n : { ...n, readAt }));
}

export function getMockPreferences(): NotificationPreferences {
  return {
    notificationPush: mockPreferences.notificationPush,
    types: { ...mockPreferences.types },
  };
}

export function updateMockPreferences(patch: NotificationPreferencesPatch): void {
  if (typeof patch.notificationPush === "boolean") {
    mockPreferences.notificationPush = patch.notificationPush;
  }
  if (patch.types) {
    mockPreferences.types = { ...mockPreferences.types, ...patch.types };
  }
}
